/**
 * tableLayout.ts — resolves a table's logical grid (cell → row/column index,
 * accounting for `rowspan` and `colspan`) before the segmenter and column-width
 * derivation consume it.
 *
 * HTML tables don't tell you "cell X is in column 3" — they tell you the source
 * order of cells per row, and you have to derive the actual column index by
 * tracking which columns are still occupied by `rowspan` cells started in
 * earlier rows. Standard table layout algorithm: an "occupancy carry" per
 * column, decremented at the end of each row.
 */

import { ElementNode, HastNode } from "./hast";

export interface Placement {
    cell: ElementNode;
    /** Index into the `trs` array (0-based). */
    rowIdx: number;
    /** Actual column index (0-based), after skipping ongoing rowspans. */
    col: number;
    colSpan: number;
    rowSpan: number;
}

export interface TableLayout {
    /** Logical column count of the table (max column extent across all rows). */
    colCount: number;
    /** One entry per cell, in row-then-source order. */
    placements: Placement[];
}

function isElement(n: HastNode): n is ElementNode {
    return n.type === "element";
}

function rowCells(tr: ElementNode): ElementNode[] {
    return tr.children.filter(
        (c): c is ElementNode => isElement(c) && (c.tagName === "td" || c.tagName === "th"),
    );
}

function spanAttr(cell: ElementNode, name: "colspan" | "rowspan"): number {
    const n = parseInt(cell.properties[name] ?? "1", 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
}

export function computeTableLayout(trs: ElementNode[]): TableLayout {
    // carry[c] = number of MORE rows column c remains occupied for (going INTO
    // the current row). 0 means free this row.
    const carry: number[] = [];
    const placements: Placement[] = [];
    let colCount = 0;

    for (let rowIdx = 0; rowIdx < trs.length; rowIdx++) {
        let col = 0;
        for (const cell of rowCells(trs[rowIdx])) {
            // Skip columns occupied by rowspans started above.
            while ((carry[col] ?? 0) > 0) col++;
            const cs = spanAttr(cell, "colspan");
            const rs = spanAttr(cell, "rowspan");
            placements.push({ cell, rowIdx, col, colSpan: cs, rowSpan: rs });
            // Reserve all spanned columns for the remaining (rs-1) rows. We set
            // `rs` here and decrement once at the end of THIS row, so the carry
            // value going into the next row is `rs - 1`.
            for (let j = 0; j < cs; j++) carry[col + j] = rs;
            col += cs;
        }
        // Account for rowspans extending past where the row's own cells reach.
        for (let k = col; k < carry.length; k++) {
            if ((carry[k] ?? 0) > 0) col = k + 1;
        }
        colCount = Math.max(colCount, col);
        for (let k = 0; k < carry.length; k++) {
            if ((carry[k] ?? 0) > 0) carry[k]--;
        }
    }

    return { colCount, placements };
}
